import { z } from 'zod';
import { type RoleDefinition, highestRank, effectivePermissions } from './roles';

/** Header the app sends on every tenant-api request while impersonating. */
export const IMPERSONATION_HEADER = 'x-tp-impersonation';

/** Inbox notification type sent to the user after an impersonation session ends. */
export const IMPERSONATION_NOTIFICATION_TYPE = 'impersonation.ended';

/** POST /api/v1/admin/impersonation */
export const startImpersonationSchema = z.object({ userId: z.string().min(1) });
export type StartImpersonation = z.infer<typeof startImpersonationSchema>;

export const impersonationSessionSchema = z.object({
  sessionId: z.string().min(1),
  target: z.object({ id: z.string().min(1), displayName: z.string().min(1), email: z.email() }),
  actor: z.object({ id: z.string().min(1), displayName: z.string().min(1) }),
  startedAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
});
export type ImpersonationSession = z.infer<typeof impersonationSessionSchema>;

export type ImpersonationDenial = 'missing-permission' | 'self' | 'target-rank' | 'target-disabled';

/**
 * Who may impersonate whom: the actor holds users.impersonate, isn't targeting themselves,
 * the target is active, and every role the target holds ranks below the actor's highest.
 */
export function impersonationDenial(
  actor: { id: string; roles: readonly Pick<RoleDefinition, 'rank' | 'permissions'>[] },
  target: { id: string; disabled: boolean; roles: readonly Pick<RoleDefinition, 'rank'>[] },
): ImpersonationDenial | null {
  if (!effectivePermissions(actor.roles).has('users.impersonate')) return 'missing-permission';
  if (actor.id === target.id) return 'self';
  if (target.disabled) return 'target-disabled';
  if (highestRank(target.roles) >= highestRank(actor.roles)) return 'target-rank';
  return null;
}

/** Inbox text for the impersonated user; contains no data beyond who and when. */
export function impersonationNoticeText(
  actorName: string,
  startedAt: string,
): { title: string; body: string } {
  return {
    title: 'An administrator viewed your account',
    body: `${actorName} viewed TeachPartner as you (view only), starting ${startedAt}.`,
  };
}
